import { isEqual } from "lodash-es";

import { Color } from "@/features/utils/color";
import type IMDFFeature from "@/libs/imdf/feature";
import type { ThemeEditorProperties } from "@/libs/imdf/types";
import { clamp } from "../geo";
import DefaultTheme from "../theme.json";

export const HIGHLIGHT_COLOR = new Color("#ffff00ff");
export const NOHIGHLIGHT_OPACITY = 0.4;
export const HIGHLIGHT_HEIGHT = 10;

class IMDFTheme {
  theme: Record<string, any>;
  //TODO ongoing
  editorProperties: Record<string, ThemeEditorProperties> = {};
  FIRST_LEVEL_PROP: "feature_type";
  SECOND_LEVEL_PROP: "category";
  overrideFeaturesProps: boolean;
  superHighlight: boolean;

  private DEFAULT_COLOR: Color = new Color("#000000");
  private defaultNumber: number = 0;

  constructor(theme?: any, overrideFeaturesProps?: boolean) {
    this.theme = this.getSafeTheme(theme);
    this.overrideFeaturesProps = overrideFeaturesProps ?? false;
    this.superHighlight = false;
    this.initEditorProperties();
  }

  // Map for worker
  static fromMap(data: IMDFTheme) {
    return new IMDFTheme(data.theme, data.overrideFeaturesProps);
  }

  private initEditorProperties() {
    Object.keys(this.theme).forEach((key) => {
      this.editorProperties[key] = { selected: false };
    });
  }

  public isDefault() {
    return isEqual(this.theme, DefaultTheme);
  }

  public setTheme(theme: any) {
    this.theme = this.getSafeTheme(theme);
  }

  private getSafeTheme(theme: any) {
    let newTheme = {};
    if (theme != null && theme != undefined) {
      if ("default" in theme) {
        newTheme = {
          ...theme,
          default: {
            ...DefaultTheme.default,
            ...theme.default,
          },
        };
      } else newTheme = { ...theme };
    } else newTheme = DefaultTheme;

    return newTheme;
  }

  setEditorProperties(editorProperties: Record<string, ThemeEditorProperties>) {
    this.editorProperties = editorProperties;
  }

  public setOverrideFeaturesProps(override: boolean) {
    this.overrideFeaturesProps = override;
  }

  private getFeatureCompoundName(feature: IMDFFeature) {
    const featureTypeName = feature.properties[this.FIRST_LEVEL_PROP];
    const categoryName = feature.properties[this.SECOND_LEVEL_PROP];
    let compoundName = featureTypeName != undefined ? featureTypeName : "";
    compoundName += categoryName != undefined ? "." + categoryName : "";
    return compoundName;
  }

  private getThemePropertyClass(feature: IMDFFeature) {
    const compoundName = this.getFeatureCompoundName(feature);

    const featureType = feature.feature_type;
    const categoryName = feature.properties["category"];

    const styles = {
      ...this.theme[`.${categoryName}`],

      ...this.theme[featureType],
      ...this.theme[categoryName],
      ...this.theme[compoundName],
    };
    return styles;
  }

  //Retrieves a certain property (e.g. fillColor) from the feature itself, or from the theme
  private getProperty(
    propertyName: string,
    feature: IMDFFeature,
    propertyType: "color" | "number",
    minN?: number,
    maxN?: number,
  ): Color | number {
    const themePropertyClass = this.getThemePropertyClass(feature)
      ? this.getThemePropertyClass(feature)[propertyName]
      : null;
    //This logic is unused for now. It will be useful when we enable GeoJSON grid
    //Essentially, we define two arrays that point to where the property should be looked for

    //This array prioritizes looking for the property in the feature itself, and then on the theme
    const propertySourcesPrioritizingFeature = [
      feature.properties[propertyName],
      themePropertyClass,
      this.theme["default"][propertyName],
    ];
    //This array prioritizes looking for the property in the theme, and then on the feature
    const propertySourcesPrioritizingTheme = [
      themePropertyClass,
      feature.properties[propertyName],
      this.theme["default"][propertyName],
    ];

    //For now, this always results in prioritizing the feature props over the theme (until GeoJSON grid is enabled)
    const propertySources = this.overrideFeaturesProps
      ? propertySourcesPrioritizingTheme
      : propertySourcesPrioritizingFeature;

    //Looks for the property in order, as defined by propertySources
    for (const propValue of propertySources) {
      if (propValue === null || propValue == undefined) continue;

      if (propertyType === "color") {
        try {
          return new Color(propValue);
        } catch (_e) {
          // If error in color instantiation, continue to next
        }
      } else if (propertyType === "number" && !isNaN(propValue)) {
        const numberValue = Number(propValue);
        // If minN and maxN are provided, clamp the value
        if (minN !== undefined && maxN !== undefined) {
          return clamp(numberValue, minN, maxN);
        } else {
          return numberValue;
        }
      }
    }

    return propertyType === "color" ? this.DEFAULT_COLOR : this.defaultNumber;
  }

  public getSubTheme(features: IMDFFeature[]) {
    function getUniqueFeatureTypesAndCategories(
      features: IMDFFeature[],
    ): string[] {
      return [
        ...new Set([
          ...features.flatMap((feature) => {
            const { feature_type, category } = {
              ...feature,
              category: feature.properties?.category,
            };
            const typesAndCats = [];
            if (feature_type) typesAndCats.push(feature_type);
            if (category) typesAndCats.push(`.${category}`);
            if (feature_type && category)
              typesAndCats.push(`${feature_type}.${category}`);
            return typesAndCats;
          }),
          "default",
        ]),
      ];
    }

    type DefaultThemeKey = keyof typeof DefaultTheme;
    const newTheme = getUniqueFeatureTypesAndCategories(features).reduce(
      (acc: Record<string, any>, uniqueKey: string) => {
        const key = uniqueKey as DefaultThemeKey;
        acc[key] = this.theme[uniqueKey] ?? (DefaultTheme[key] || {});
        return acc;
      },
      {},
    );

    return new IMDFTheme(newTheme);
  }

  //Property getters
  public getFillColor(
    feature: IMDFFeature,
    options: {
      enableSuperHighlight: boolean;
    },
  ): Color {
    // TODO: this should be removed when migrated to maplibre
    return options.enableSuperHighlight
      ? HIGHLIGHT_COLOR
      : (this.getProperty("fillColor", feature, "color") as Color);
  }

  public getStrokeColor(feature: IMDFFeature): Color {
    return this.getProperty("strokeColor", feature, "color") as Color;
  }

  public getStrokeOpacity(
    feature: IMDFFeature,
    options: {
      forceTranslucent: boolean;
    },
  ): number {
    // TODO: this should be removed when migrated to maplibre
    return options.forceTranslucent
      ? NOHIGHLIGHT_OPACITY
      : (this.getProperty("strokeOpacity", feature, "number", 0, 1) as number);
  }

  public getFillOpacity(
    feature: IMDFFeature,
    options: {
      forceTranslucent: boolean;
    },
  ): number {
    // TODO: this should be removed when migrated to maplibre
    return options.forceTranslucent
      ? NOHIGHLIGHT_OPACITY
      : (this.getProperty("fillOpacity", feature, "number", 0, 1) as number);
  }

  public getMargin(feature: IMDFFeature): number {
    return this.getProperty(
      "margin",
      feature,
      "number",
      -Infinity,
      Infinity,
    ) as number;
  }
  public getStrokeWidth(feature: IMDFFeature): number {
    return this.getProperty(
      "strokeWidth",
      feature,
      "number",
      0,
      Infinity,
    ) as number;
  }

  public getExtrusionHeight(
    feature: IMDFFeature,
    options?: {
      enableSuperHighlight?: boolean;
    },
  ): number {
    // TODO: this should be removed when migrated to maplibre
    return options?.enableSuperHighlight
      ? HIGHLIGHT_HEIGHT
      : (this.getProperty(
          "extrusionHeight",
          feature,
          "number",
          0,
          Infinity,
        ) as number);
  }

  public clone() {
    const clonedTheme = new IMDFTheme(this.theme, this.overrideFeaturesProps);

    clonedTheme.editorProperties = { ...this.editorProperties };
    clonedTheme.superHighlight = this.superHighlight;

    return clonedTheme;
  }
}

export default IMDFTheme;
